import {Box} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {GridSortItem, GridSortModel} from '@mui/x-data-grid';
import {FC, useState} from 'react';
import {Table} from '../../tables/Table';
import {TableLayout} from '../../tables/TableLayout';
import {ChargerTableProps} from './ChargerTable.types';
import {columnConfigChargers} from './ChargerTable.tableConfig';
import {ChargerFilterValue} from '../ChargerTableActions/ChargerTableActions.types';
import {ChargerSortFieldEnum} from 'src/enums';
import {ChargePointSearchRequestData, ChargersSearchRequestDataWithoutBusinessID} from 'src/types';
import {ChargerTableActions} from '../ChargerTableActions';
import {useBusinessId, useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {ChargerListCard} from '../ChargerListCard';
import {ResponsiveTable} from '../../common/ResponsiveTable';
import {CardList} from 'src/components/common/CardList';

export const ChargerTable: FC<ChargerTableProps> = ({
                                                        data,
                                                        pageNumber,
                                                        pageSize,
                                                        handleUpdateTableData,
                                                        handleChangePage,
                                                        handleChangeRowsNumber,
                                                        isCompanyUser = false,
                                                        title,
                                                        onAddClick,
                                                        buttonText,
                                                        onlyTitle,
                                                        icon,
                                                       flush,
                                                       filters,
                                                    }) => {
    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    // Seeded from the filter the page holds, not from empty: a remount is what coming back from a
    // detail page is, and starting at '' meant the next filter change wiped the search that was
    // applied - `updateData` writes `keyword: search`, so an empty local copy cleared the URL.
    const [searchValue, setSearchValue] = useState<string>(filters.keyword ?? '');
    const [sortValue, setSortValue] = useState<GridSortItem>({field: ChargerSortFieldEnum.CREATED_AT, sort: 'desc'});
    // The delete column and the card's remove action used to be gated on `!isRealEstate` alone -
    // a persona standing in for a permission that exists and says the same thing more precisely.
    const canDeleteCharger = useCan('chargers.delete');

    const handleSearch = (value: string) => {
        setSearchValue(value);
        handleUpdateTableData(updateData(value, sortValue, filters));
    };

    const handleFilter = (value: ChargerFilterValue) => {
        handleUpdateTableData(updateData(searchValue, sortValue, {...filters, ...value}));
    };

    const handleSort = (value: GridSortModel) => {
        setSortValue(value[0]);
        handleUpdateTableData(updateData(searchValue, value[0], filters));
    };

    /**
     * The filter the list is asked for: a merge onto what it is already on, not a fresh object.
     *
     * It used to be built from scratch out of the toolbar's own five fields, so every keystroke in the
     * search box replaced the whole filter - and with it a status set by the strip's tiles, the
     * `locationId` the location page sets, and every other field the toolbar does not render. Three
     * screens hold this state and pass it in; nothing here keeps a second copy of it.
     */
    const updateData = (
        search: string,
        sort: GridSortItem,
        filter: ChargersSearchRequestDataWithoutBusinessID
    ) => ({
        ...filter,
        chargePointStatus: filter.chargePointStatus || undefined,
        connectorStatus: filter.connectorStatus || undefined,
        keyword: search,
        sortAsc: sort ? sort.sort === 'asc' : false,
        sortField: (sort && sort.field) || ChargerSortFieldEnum.CREATED_AT,
    });

    const currentFilters: ChargePointSearchRequestData = {
        ...updateData(searchValue, sortValue, filters),
        businessId: businessId,
        companyId: authInfo?.company?.id,
    };

    return (
        <Box>
            <TableLayout
                flush={flush}
                toolbar={!flush && (
                    <ChargerTableActions
                        title={title}
                        icon={icon}
                        onAdd={onlyTitle ? undefined : onAddClick}
                        addLabel={buttonText}
                        handleSearch={handleSearch}
                        handleFilter={handleFilter}
                        filters={currentFilters}
                        totalElements={data.page.totalElements}
                    />
                )}
                totalPages={data.page.totalPages}
                page={pageNumber && pageNumber === 0 ? pageNumber : data.page.number}
                pageSize={pageSize}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
            <ResponsiveTable
                responsiveElement={
                    <CardList>
                        {data?.content?.map((item) => (
                            <ChargerListCard
                                key={item?.id}
                                {...item}
                                canRemoveCharger={canDeleteCharger}
                            />
                        ))}
                    </CardList>
                }
                desktopElement={
                    <Table
                        rows={data.content}
                        hideFooter
                        columns={columnConfigChargers(canDeleteCharger, isCompanyUser)}
                        handleSort={handleSort}
                        sx={{border: 'none', borderRadius: 0}}
                    />
                }
            />
            </TableLayout>
        </Box>
    );
};